<template>
  <div class="app-body organisation">
    <!-- render researcher header -->
    <organisation-header :organisation="organisation" :isLoaded="isLoaded" :hasResearch="hasResearch"
      :hasNews="hasNews" />

    <!-- render template -->
    <router-view
      :organisation="organisation"
      :searchFilterObject="searchFilterObject"
      :isLoaded="isLoaded"
      :hasResearch="hasResearch"
      :researchLoaded="researchLoaded"
      :indexes="indexes" />
  </div>
</template>

<script>
import OrganisationHeader from '@/components/organisation/Header'
import OrganisationService from '@/services/organisation'
import SearchService from '@/services/search'
import highlights from '@/mixins/highlights'
import organisationMixins from '@/mixins/organisation'

export default {
  name: 'organisationBase',
  mixins: [highlights, organisationMixins],
  components: {
    OrganisationHeader
  },
  data () {
    return {
      isLoaded: false,
      organisation: {},
      indexes: {},
      hasResearch: false,
      hasNews: false,
      researchLoaded: false,
      objectForHighlighting: {
        nameProperties: ['name'],
        properties: ['about'],
        arrays: ['leaderships.about', 'technologies.description']
      },
      suggestionIndexes: ['patents', 'projects', 'publications', 'clinical_trials', 'news'],
      searchFilterObject: {
        key: 'organisations',
        values: '',
        index: 'publications',
        global: false,
        suggestions: []
      }
    }
  },
  metaInfo () {
    return {
      titleTemplate: this.isLoaded ? `${this.organisation.name} | AcademicLabs` : `%s`
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== from.params.id) {
      this.getData(to.params.id)
    }
    this.checkOpenModal(to.query)
    next()
  },
  mounted () {
    this.checkOpenModal(this.$route.query)
  },
  created () {
    this.getData(this.$route.params.id)
  },
  methods: {
    getData (id) {
      this.isLoaded = false

      OrganisationService.getBySlugValue(id).then(organisation => {
        this.organisation = organisation
        this.setSuggestions(this.organisation).then(this.fetchResearchCounts)
        this.fetchHighlights()
      }).catch(err => {
        this.error = this.handleError(err)
        this.isLoaded = !this.isEmpty(this.error)
      })
    },
    setSuggestions (organisation) {
      this.searchFilterObject.label = organisation.name

      const slugValues = [...organisation.slug_values]
      const scopedSlugTexts = [organisation.slug_text]
      const suggestions = []

      organisation.names.forEach(name => {
        suggestions.push({
          type: 'organisation',
          must: name,
          must_not: organisation.slug_values
        })
      })

      if (this.isIncubator) {
        this.searchFilterObject.index = 'organisations'
        this.searchFilterObject.key = 'incubators'
        this.searchFilterObject.suggestion_indexes = ['organisations']
      } else if (this.isInvestor) {
        this.searchFilterObject.index = 'organisations'
        this.searchFilterObject.key = 'investors'
        this.searchFilterObject.suggestion_indexes = ['organisations']
      } else {
        this.searchFilterObject.index = 'publications'
        this.searchFilterObject.key = 'organisations'
        this.searchFilterObject.suggestion_indexes = this.suggestionIndexes
      }

      if (organisation.children) {
        organisation.children.forEach(childOrg => {
          slugValues.push(...childOrg.slug_values)
          scopedSlugTexts.push(childOrg.slug_text)
          suggestions.push({
            type: 'organisation',
            must: childOrg.name,
            must_not: childOrg.slug_values
          })
        })
      }

      this.searchFilterObject.values = slugValues
      this.searchFilterObject.suggestions = suggestions
      this.searchFilterObject.scoped_slug_texts = scopedSlugTexts
      this.searchFilterObject.item_type = organisation.category

      const filterkey = this.searchFilterObject.key

      this.searchFilterObject.filter_order = [filterkey]
      this.searchFilterObject.filters = {}
      this.searchFilterObject.filters[filterkey] = {}
      this.searchFilterObject.filters[filterkey].buckets = []
      this.searchFilterObject.filters[filterkey].buckets.push({ active: true, values: slugValues })
      return Promise.resolve()
    },
    fetchResearchCounts () {
      this.indexes = {}
      // Fetch counts for research of a RG to determine the default index we want to redirect to on "Research" click
      SearchService.counts(this.searchFilterObject).then(counts => {
        if (!counts || !counts.categories) return
        this.indexes = counts.categories
        const [index, indexData] = this.getMostRelevantIndex(counts.categories, 'publications')
        this.searchFilterObject.index = index
        this.hasResearch = indexData.total > 0 // Boolean used to render 'Research' tab in SubNav
        this.hasNews = counts.categories?.news?.total > 0 // Boolean used to update SubNav title to 'Portfolio & News' when applicable
        this.researchLoaded = true
      }).catch(() => {
        // It's fiiine
      })
    },
    fetchHighlights () {
      SearchService.highlightsDetailView(this.getSearchQueryForHighlighting('organisations', this.organisation.slug_values)).then(highlights => {
        let parsedHighlights = this.generateHighlights(this.organisation, highlights, this.objectForHighlighting)
        for (let esProperty of this.objectForHighlighting.nameProperties) this.setForString(esProperty, esProperty)
        this.$set(this.organisation, 'highlights', parsedHighlights)

        if (highlights && highlights['web_pages.body'] && this.organisation.qa) {
          this.$set(this.organisation.qa, 'webpage', highlights['web_pages.body'])
        }

        this.isLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
      })
    }
  }
}
</script>
